import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiClient } from "@/lib/api-client";
import { CONNECTIONS_STATUS_QUERY_KEY } from "./connections-status-query";

const OAUTH_QUERY_KEYS = ["oauth", "result", "reason", "warning"] as const;

/** Strips oauth return query params from a path+search string. */
export function sanitizeReturnPath(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  for (const key of OAUTH_QUERY_KEYS) {
    params.delete(key);
  }
  const q = params.toString();
  return q ? `${pathname}?${q}` : pathname;
}

/**
 * Global OAuth return handler for channel connect (Instagram / WhatsApp).
 * Mount once in AppLayout.
 */
export function useChannelOAuthReturn() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const oauth = searchParams.get("oauth");
    const result = searchParams.get("result");
    if (!oauth || !result) return;

    const warning = searchParams.get("warning");
    const signature = `${oauth}:${result}:${searchParams.get("reason") ?? ""}:${warning ?? ""}`;
    if (handledRef.current === signature) return;
    handledRef.current = signature;

    const reason = searchParams.get("reason");
    const webhookPending = warning === "webhook_subscription_pending";
    const next = new URLSearchParams(searchParams);
    for (const key of OAUTH_QUERY_KEYS) {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });

    void queryClient.invalidateQueries({ queryKey: [CONNECTIONS_STATUS_QUERY_KEY] });

    if (result === "success") {
      if (oauth === "instagram") {
        void apiClient<{ instagram?: { handle?: string } }>("/connections/status")
          .then((res) => {
            const handle = res.success ? res.data?.instagram?.handle : undefined;
            const detail = handle
              ? t("channelConnect.oauth.successInstagramHandle", {
                  handle: handle.startsWith("@") ? handle : `@${handle}`,
                })
              : undefined;
            toast.success(t("channelConnect.oauth.successInstagram"), detail ? { description: detail } : undefined);
            if (webhookPending) {
              toast.info(t("channelConnect.oauth.webhookPending"));
            }
          })
          .catch(() => {
            toast.success(t("channelConnect.oauth.successInstagram"));
            if (webhookPending) {
              toast.info(t("channelConnect.oauth.webhookPending"));
            }
          });
      } else if (oauth === "whatsapp") {
        toast.success(t("channelConnect.oauth.successWhatsapp"));
      } else if (oauth === "email") {
        void apiClient<{ email?: { address?: string } }>("/connections/status", { studioId: "all" })
          .then((res) => {
            const address = res.success ? res.data?.email?.address : undefined;
            const detail = address
              ? t("channelConnect.email.oauth.successAddress", { address })
              : undefined;
            toast.success(t("channelConnect.email.oauth.success"), detail ? { description: detail } : undefined);
          })
          .catch(() => {
            toast.success(t("channelConnect.email.oauth.success"));
          });
      }
      return;
    }

    if (result === "error") {
      const isDenied = reason === "User denied access" || reason === "access_denied";
      const message = isDenied
        ? t("channelConnect.errors.accessDenied")
        : reason
          ? String(reason)
          : t("channelConnect.errors.connectFailed");
      toast.error(message);
    }
  }, [searchParams, setSearchParams, queryClient, t]);
}
